/**
 * Admin Event Registrations Tab
 *
 * Shows all registrations for the active business event with:
 * - Stats overview (total, paid, pending, capacity)
 * - Filterable/searchable registration list
 * - Payment status management
 * - Ticket email resend
 * - Registration detail view
 */
import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Users,
  Search,
  Loader2,
  RefreshCw,
  Mail,
  Calendar,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  Eye,
  ArrowLeft,
  Phone,
  Globe,
  FileText,
  TicketCheck,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

// Theme colors matching AYU admin
const T = {
  olive: "#6b7f4e",
  gold: "#c9a96e",
  dark: "#2d3a1f",
  mid: "#8a9680",
  bg: "#f5f2ec",
  white: "#ffffff",
  border: "#e8e2d8",
};

type PaymentFilter = "all" | "paid" | "pending" | "failed" | "refunded";

const PAYMENT_BADGE: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> = {
  paid: { label: "Bezahlt", color: "#16a34a", bg: "#dcfce7", icon: CheckCircle2 },
  pending: { label: "Ausstehend", color: "#d97706", bg: "#fef3c7", icon: Clock },
  failed: { label: "Fehlgeschlagen", color: "#dc2626", bg: "#fee2e2", icon: XCircle },
  refunded: { label: "Erstattet", color: "#6b7280", bg: "#f3f4f6", icon: RefreshCw },
};

export default function AdminEventTab() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<PaymentFilter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteDialogId, setDeleteDialogId] = useState<number | null>(null);

  const { data, isLoading, refetch } = trpc.event.adminListRegistrations.useQuery();
  const utils = trpc.useUtils();

  const updateStatus = trpc.event.adminUpdatePaymentStatus.useMutation({
    onSuccess: () => {
      toast.success("Status aktualisiert");
      utils.event.adminListRegistrations.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteReg = trpc.event.adminDeleteRegistration.useMutation({
    onSuccess: () => {
      toast.success("Registrierung gelöscht");
      setDeleteDialogId(null);
      setSelectedId(null);
      utils.event.adminListRegistrations.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const sendTicket = trpc.event.adminSendTicket.useMutation({
    onSuccess: () => {
      toast.success("Ticket-Email gesendet");
      utils.event.adminListRegistrations.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const filtered = useMemo(() => {
    if (!data?.registrations) return [];
    let list = data.registrations;
    if (filter !== "all") {
      list = list.filter((r) => r.paymentStatus === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.firstName.toLowerCase().includes(q) ||
          r.lastName.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.phone.includes(q)
      );
    }
    return list;
  }, [data?.registrations, filter, search]);

  const selectedReg = useMemo(
    () => data?.registrations.find((r) => r.id === selectedId),
    [data?.registrations, selectedId]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Keine Daten verfügbar
      </div>
    );
  }

  const { stats, event } = data;

  // Detail view
  if (selectedReg) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedId(null)}
          className="flex items-center gap-2 text-sm font-medium hover:underline text-primary"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück zur Liste
        </button>

        <div className="flex items-center justify-between">
          <h2 className="text-xl font-light font-display font-normal text-foreground">
            {selectedReg.firstName} {selectedReg.lastName}
          </h2>
          <div className="flex items-center gap-2">
            {selectedReg.paymentStatus === "paid" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => sendTicket.mutate({ registrationId: selectedReg.id })}
                disabled={sendTicket.isPending}
                className="border-border text-primary hover:bg-muted"
              >
                {sendTicket.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                <span className="ml-1.5">Ticket senden</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeleteDialogId(selectedReg.id)}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-border">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-sm text-foreground">Kontaktdaten</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4 shrink-0 text-primary" />
                  <a href={`mailto:${selectedReg.email}`} className="hover:underline">{selectedReg.email}</a>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="w-4 h-4 shrink-0 text-primary" />
                  <a href={`tel:${selectedReg.phone}`} className="hover:underline">{selectedReg.phone}</a>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Globe className="w-4 h-4 shrink-0 text-primary" />
                  {selectedReg.language === "ur" ? "Urdu" : "English"}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4 shrink-0 text-primary" />
                  {new Date(selectedReg.createdAt).toLocaleString("de-DE")}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-sm text-foreground">Zahlungsstatus</h3>
              <div className="flex items-center gap-3">
                {(() => {
                  const b = PAYMENT_BADGE[selectedReg.paymentStatus];
                  const Icon = b?.icon || Clock;
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{ color: b?.color, backgroundColor: b?.bg }}
                    >
                      <Icon className="w-4 h-4" />
                      {b?.label || selectedReg.paymentStatus}
                    </span>
                  );
                })()}
                {selectedReg.ticketSent && (
                  <span className="inline-flex items-center gap-1 text-xs text-primary">
                    <TicketCheck className="w-3.5 h-3.5" /> Ticket gesendet
                  </span>
                )}
              </div>

              <div className="pt-2">
                <label className="block text-xs mb-1 text-muted-foreground">Status ändern:</label>
                <Select
                  value={selectedReg.paymentStatus}
                  onValueChange={(val) =>
                    updateStatus.mutate({
                      registrationId: selectedReg.id,
                      paymentStatus: val as "pending" | "paid" | "failed" | "refunded",
                    })
                  }
                >
                  <SelectTrigger className="w-48 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="paid">Bezahlt</SelectItem>
                    <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                    <SelectItem value="refunded">Erstattet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Application text */}
        <Card className="border-border">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-3 text-foreground">
              <FileText className="w-4 h-4 inline mr-1.5 text-primary" />
              Bewerbungstext
            </h3>
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
              {selectedReg.applicationText}
            </p>
          </CardContent>
        </Card>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogId !== null} onOpenChange={() => setDeleteDialogId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrierung löschen?</DialogTitle>
              <DialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Die Registrierung von{" "}
                <strong>{selectedReg.firstName} {selectedReg.lastName}</strong> wird dauerhaft gelöscht.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogId(null)}>
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteDialogId && deleteReg.mutate({ registrationId: deleteDialogId })}
                disabled={deleteReg.isPending}
              >
                {deleteReg.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-light font-display font-normal text-foreground">
            {event.title}
          </h2>
          <p className="text-sm mt-1 text-muted-foreground">
            {event.date} &middot; {event.time} &middot; {event.location}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refetch()}
          className="border-border text-primary hover:bg-muted"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-border">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
            <div className="text-2xl font-light text-foreground">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Gesamt</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-600" />
            <div className="text-2xl font-light text-green-700">{stats.paid}</div>
            <div className="text-xs text-muted-foreground">Bezahlt</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4 text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-amber-600" />
            <div className="text-2xl font-light text-amber-700">{stats.pending}</div>
            <div className="text-xs text-muted-foreground">Ausstehend</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4 text-center">
            <CreditCard className="w-5 h-5 mx-auto mb-1 text-primary" />
            <div className="text-2xl font-light text-foreground">
              {stats.paid}/{stats.maxAttendees}
            </div>
            <div className="text-xs text-muted-foreground">Kapazität</div>
          </CardContent>
        </Card>
      </div>

      {/* Capacity bar */}
      <div className="w-full h-2 rounded-full bg-[#e8e2d8] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min((stats.paid / stats.maxAttendees) * 100, 100)}%`,
            backgroundColor: stats.paid >= stats.maxAttendees ? "#dc2626" : T.olive,
          }}
        />
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Name, E-Mail oder Telefon suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-border"
          />
        </div>
        <Select value={filter} onValueChange={(v) => setFilter(v as PaymentFilter)}>
          <SelectTrigger className="w-48 border-border">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="paid">Bezahlt</SelectItem>
            <SelectItem value="pending">Ausstehend</SelectItem>
            <SelectItem value="failed">Fehlgeschlagen</SelectItem>
            <SelectItem value="refunded">Erstattet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Registration list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Keine Registrierungen gefunden</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((reg) => {
            const badge = PAYMENT_BADGE[reg.paymentStatus];
            const Icon = badge?.icon || Clock;
            return (
              <Card
                key={reg.id}
                className="border-border hover:border-border transition-colors cursor-pointer"
                onClick={() => setSelectedId(reg.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: T.olive }}
                      >
                        {reg.firstName[0]}{reg.lastName[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate text-foreground">
                            {reg.firstName} {reg.lastName}
                          </span>
                          {reg.language === "ur" && (
                            <Badge variant="outline" className="text-[10px] border-[#c9a96e] text-[#c9a96e]">
                              اردو
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{reg.email}</span>
                          <span className="hidden sm:inline">{reg.phone}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ color: badge?.color, backgroundColor: badge?.bg }}
                      >
                        <Icon className="w-3 h-3" />
                        {badge?.label}
                      </span>
                      {reg.ticketSent && (
                        <span title="Ticket gesendet"><TicketCheck className="w-4 h-4 shrink-0 text-primary" /></span>
                      )}
                      <Eye className="w-4 h-4 shrink-0 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
